import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import MultiSelect, { type SelectId, type MultiSelectOption } from './MultiSelect';
import Button from './ui/Button';
import { ConfirmModal } from './ui';
import { translateError } from '../utils/errorMessages';
import { membersOnlyFromRemovedGroups } from '../utils/groupMembers';

interface RoleAccessWidgetProps {
  entityType: 'activities' | 'groups' | 'members' | 'events' | 'eventTemplates';
  entityId: string | number;
}

interface RoleDetail {
  _id: number;
  name: string;
  description?: string;
  permissions: Record<string, number>;
  type: 'admin' | 'user';
  limitDataAccess: boolean;
  accessGrants?: { activities: number[]; groups: number[]; members: number[]; events: number[]; eventTemplates: number[] };
}

/** A role that lost this group label, with the members only the group brought. */
interface LabelDrop {
  role: RoleDetail;
  memberIds: number[];
}

interface RoleListItem {
  _id: number;
  name: string;
  limitDataAccess: boolean;
  isSystem: boolean;
}

const RoleAccessWidget: React.FC<RoleAccessWidgetProps> = ({ entityType, entityId }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();

  const [roleOptions, setRoleOptions] = useState<MultiSelectOption[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<SelectId[]>([]);
  const [originalRoleIds, setOriginalRoleIds] = useState<SelectId[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [labelDrops, setLabelDrops] = useState<LabelDrop[] | null>(null);
  const [dropping, setDropping] = useState(false);

  const canWrite = hasPermission('roles:write');

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [rolesRes, accessRes] = await Promise.all([
        api.roles.list(),
        api.roles.getEntityAccess(entityType, entityId),
      ]);

      // Only show roles that have limitDataAccess enabled (non-system)
      const dataLimitedRoles = (rolesRes.data as RoleListItem[])
        .filter(r => r.limitDataAccess && !r.isSystem);

      setRoleOptions(dataLimitedRoles.map(r => ({ _id: r._id, name: r.name })));

      const currentIds = (accessRes.data as { roleIds: number[] }).roleIds;
      setSelectedRoleIds(currentIds);
      setOriginalRoleIds(currentIds);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const hasChanges = JSON.stringify([...selectedRoleIds].map(Number).sort((a, b) => a - b))
    !== JSON.stringify([...originalRoleIds].map(Number).sort((a, b) => a - b));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.roles.setEntityAccess(entityType, entityId, selectedRoleIds.map(Number));
      addToast(t('roles.entityAccessSaved'), 'success');
      const removed = originalRoleIds.map(Number).filter(r => !selectedRoleIds.map(Number).includes(r));
      await fetchData(true);
      if (entityType === 'groups' && removed.length > 0) void collectLabelDrops(removed);
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Group label removed from roles: find members only this group brought (default keep).
  const collectLabelDrops = async (roleIds: number[]) => {
    try {
      const drops: LabelDrop[] = [];
      for (const roleId of roleIds) {
        const role = (await api.roles.get(String(roleId))).data as RoleDetail;
        const grants = role.accessGrants;
        if (!grants) continue;
        const remaining = grants.groups.filter(g => Number(g) !== Number(entityId));
        const memberIds = await membersOnlyFromRemovedGroups([entityId], remaining, grants.members);
        if (memberIds.length > 0) drops.push({ role, memberIds });
      }
      if (drops.length > 0) setLabelDrops(drops);
    } catch {
      // Lookup failed: members stay, which is the default answer anyway.
    }
  };

  const handleDropLabelMembers = async () => {
    if (!labelDrops) return;
    setDropping(true);
    try {
      for (const { role, memberIds } of labelDrops) {
        // Fresh read; only the members dimension changes (absent fields = unchanged).
        const fresh = (await api.roles.get(String(role._id))).data as RoleDetail;
        if (!fresh.accessGrants) continue;
        const drop = new Set(memberIds);
        await api.roles.update(String(role._id), {
          // Required by the route's validator; echoed unchanged.
          name: fresh.name,
          permissions: fresh.permissions,
          type: fresh.type,
          accessGrants: { members: fresh.accessGrants.members.filter(m => !drop.has(Number(m))) },
        });
      }
      addToast(t('roles.entityAccessSaved'), 'success');
      setLabelDrops(null);
    } catch (err: any) {
      addToast(translateError(err, t, 'roles.updateError'), 'error');
    } finally {
      setDropping(false);
    }
  };

  if (!hasPermission('roles:read')) return null;
  if (loading) return <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{t('common.loading')}...</p>;
  if (roleOptions.length === 0) return null;

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        {t('roles.entityAccessDescription')}
        {entityType === 'groups' && <> {t('roles.entityAccessGroupCopies')}</>}
      </p>
      <MultiSelect
        options={roleOptions}
        selectedIds={selectedRoleIds}
        onChange={canWrite ? setSelectedRoleIds : () => {}}
        placeholder={t('roles.entityAccessPlaceholder')}
      />
      {canWrite && hasChanges && (
        <div style={{ marginTop: '0.75rem' }}>
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t('common.save')}
          </Button>
        </div>
      )}
      <ConfirmModal
        isOpen={labelDrops !== null}
        onClose={() => setLabelDrops(null)}
        onConfirm={handleDropLabelMembers}
        title={t('roles.removeGroupMembersTitle')}
        message={t('roles.removeGroupMembersFromRoles', {
          roles: (labelDrops ?? []).map(d => `${d.role.name} (${d.memberIds.length})`).join(', '),
        })}
        confirmText={t('roles.removeGroupMembersConfirm')}
        cancelText={t('roles.removeGroupMembersKeep')}
        loading={dropping}
      />
    </div>
  );
};

export default RoleAccessWidget;
